/* Atlas v3 — The Leverage Desk: every Atlas name that has a 2× daily
   single-stock ETF, ranked by the leveraged ETF's move over a chosen
   timeframe (1D/1W/1M/6M/1Y). Tap a row to open the underlying's TickerSheet.

   Data: useLeveragedEtfs() (the leveraged_etfs map) + useLivePrices on the
   PRIMARY ETF ticker per stock in mode='historical' (gives change1d/1w/1m/
   6m/1y). The inline sparkline is a real per-ticker /api/chart-bars series
   for the PRIMARY ETF over the SAME selected timeframe as the move column
   (fixed 2026-08-21 — it used to always fetch range=1M regardless of tf).

   Composed in the spirit of the studio (benscomponents) registry: segmented
   timeframe control (segment-group), chip-group theme filters, per-row
   spark-chart sparkline, badge issuer chips, ticker-pulse basket pill.
   Brings its own shell (TickerStrip + BottomNav) like the other v3 screens. */
import { useState, useMemo, useRef, useEffect } from 'react'
import { TickerCtx, Dropdown } from './lib'
import { StockLogo } from '@/v2/components/StockLogo'
import { TickerStrip, BottomNav, SearchPill, DeskSwitcher } from './shell'
import { StockDetailProvider, useStockDetail } from '@/v2/lib/StockDetailProvider'
import { useLeveragedEtfs } from '@/v2/hooks/useLeveragedEtfs'
import { useLivePrices } from '@/v2/hooks/useLivePrices'
import { HeroCandles } from './HeroCandles'

const Mono = { fontFamily: 'var(--studio-font-mono)' }
const Display = { fontFamily: 'var(--studio-font-display)', fontStyle: 'italic' }

const TFS = ['1D', '5D', '1M', '6M', '1Y']
// Map the desk's timeframe chips to the live-snapshot change fields. The
// snapshot exposes change1w (5 trading days) — we surface it under "5D".
const CHANGE_FIELD = { '1D': 'change1d', '5D': 'change1w', '1M': 'change1m', '6M': 'change6m', '1Y': 'change1y' }

/* ghost-1× sparkline paths. `closes` is the 2× ETF's real price series; the
   1× "ghost" is synthesized by halving each deviation from the start (the
   leveraged fund moves ~2× its underlying), so the bold 2× line visibly swings
   wider than the faint ghost — the chart shows the amplification with no extra
   fetch. Both drawn on a shared y-domain. */
function ghostPaths(closes, w, h, pad = 2) {
  if (!closes || closes.length < 2) return null
  const base = closes[0]
  const ghost = closes.map((v) => base + (v - base) / 2)
  const all = closes.concat(ghost)
  const min = Math.min(...all), max = Math.max(...all), range = (max - min) || 1
  const P = (s) => s.map((v, i) => `${i === 0 ? 'M' : 'L'} ${(pad + (i / (s.length - 1)) * (w - pad * 2)).toFixed(1)} ${(pad + (1 - (v - min) / range) * (h - pad * 2)).toFixed(1)}`).join(' ')
  return {
    ghost: P(ghost),
    line: P(closes),
    lx: pad + (w - pad * 2),
    ly: pad + (1 - (closes[closes.length - 1] - min) / range) * (h - pad * 2),
  }
}

function fmtPct(v) {
  if (v == null) return '—'
  const a = Math.abs(v)
  return `${v >= 0 ? '+' : '−'}${a >= 10 ? a.toFixed(0) : a.toFixed(1)}%`
}

/* shared column grid — rank · stock · 2× ETF · spark · move.
   Every row + the header use this so the columns line up exactly. */
const GRID = {
  display: 'grid',
  gridTemplateColumns: '30px minmax(64px, 1.1fr) minmax(50px, 0.9fr) 48px 56px',
  alignItems: 'center',
  gap: 10,
}
const HDR = { ...Mono, fontSize: 8.5, letterSpacing: '0.14em', color: 'hsl(var(--studio-text-subtle))', textTransform: 'uppercase' }

/* ── Spark — REAL price line for the 2× ETF over the desk's selected
   timeframe (daily/intraday closes from /api/chart-bars, keyed by
   ticker+tf) with the synthesized 1× ghost behind it. Lazy-loaded when it
   scrolls into view so the desk doesn't fire ~80 requests at once; cached
   per ETF ticker+timeframe across re-renders. Used by both the rows and the
   Leaders/Laggards band. */
const SPARK_CACHE = new Map() // `${ticker}::${tf}` → number[] (closes)
function Spark({ ticker, tf, col, w = 46, h = 20, bold = 1.3 }) {
  const cacheKey = `${ticker}::${tf}`
  const ref = useRef(null)
  const [closes, setCloses] = useState(() => SPARK_CACHE.get(cacheKey) || null)
  // Resync when the ticker/timeframe changes without a remount (sort/timeframe flips)
  useEffect(() => { setCloses(SPARK_CACHE.get(cacheKey) || null) }, [cacheKey])
  useEffect(() => {
    if (closes || !ticker) return
    const el = ref.current
    if (!el) return
    let done = false
    const load = () => {
      if (done) return
      done = true
      fetch(`/api/chart-bars?ticker=${encodeURIComponent(ticker)}&range=${tf}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          const cs = Array.isArray(d?.bars) ? d.bars.map((b) => b.c).filter((c) => c != null) : []
          SPARK_CACHE.set(cacheKey, cs)
          setCloses(cs)
        })
        // Transient failure: dashed baseline, but don't cache [] for the session
        .catch(() => { setCloses([]) })
    }
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { load(); io.disconnect() } }, { rootMargin: '120px' })
    io.observe(el)
    return () => io.disconnect()
  }, [cacheKey, ticker, tf, closes])

  const pp = ghostPaths(closes, w, h)
  return (
    <svg ref={ref} width={w} height={h} style={{ display: 'block' }}>
      {pp ? (
        <>
          <path d={pp.ghost} fill="none" stroke="hsl(var(--studio-text-subtle))" strokeWidth="0.9" strokeDasharray="2 2" opacity="0.5" />
          <path d={pp.line} fill="none" stroke={`hsl(var(${col}))`} strokeWidth={bold} strokeLinecap="round" strokeLinejoin="round" />
          <circle cx={pp.lx} cy={pp.ly} r={w > 80 ? 2.2 : 1.7} fill={`hsl(var(${col}))`} />
        </>
      ) : (
        <line x1="2" y1={h / 2} x2={w - 2} y2={h / 2} stroke="hsl(var(--studio-border))" strokeWidth="1" strokeDasharray="2 3" />
      )}
    </svg>
  )
}

/* ── one ranked row ── */
function DeskRow({ rank, inst, move, tf, onOpen }) {
  const [hot, setHot] = useState(false)
  const up = move != null && move >= 0
  const col = move == null ? '--studio-text-subtle' : up ? '--studio-success' : '--studio-danger'
  const primary = inst.etfs[0]
  return (
    <button onClick={() => onOpen(inst.underlying)}
      onPointerDown={() => setHot(true)} onPointerUp={() => setHot(false)} onPointerCancel={() => setHot(false)} onPointerLeave={() => setHot(false)}
      style={{
        ...GRID, width: '100%', padding: '13px 20px', cursor: 'pointer', textAlign: 'left', border: 'none',
        borderTop: '1px solid hsl(var(--studio-border) / 0.4)',
        background: hot ? 'hsl(var(--studio-surface-2) / 0.55)' : 'transparent', transition: 'background 140ms',
      }}>
      {/* underlying company logo */}
      <StockLogo ticker={inst.underlying} size={26} rounded="50%" />
      {/* stock */}
      <div style={{ minWidth: 0 }}>
        <div style={{ ...Mono, fontSize: 13, color: 'hsl(var(--studio-text))' }}>{inst.underlying}</div>
        <div style={{ fontFamily: 'var(--studio-font-sans)', fontSize: 10, color: 'hsl(var(--studio-text-subtle))', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{inst.name}</div>
      </div>
      {/* 2× ETF symbol */}
      <span style={{ ...Mono, fontSize: 12.5, color: 'hsl(var(--studio-accent-soft))', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{primary.ticker}</span>
      {/* spark — real 2× line (matching the selected timeframe) + synthesized 1× ghost */}
      <Spark ticker={primary.ticker} tf={tf} col={col} />
      {/* move */}
      <span style={{ ...Mono, fontSize: 13, fontVariantNumeric: 'tabular-nums', color: `hsl(var(${col}))`, textAlign: 'right' }}>{fmtPct(move)}</span>
    </button>
  )
}

/* ── Leaders & Laggards band cell — top gainer / top loser hero ── */
function BandCell({ row, up, tf, onOpen }) {
  const { inst, move } = row
  const c = up ? '--studio-success' : '--studio-danger'
  return (
    <button onClick={() => onOpen(inst.underlying)}
      style={{ flex: 1, minWidth: 0, padding: '13px 14px', cursor: 'pointer', textAlign: 'left', background: 'transparent',
        border: 'none', borderLeft: up ? 'none' : '1px solid hsl(var(--studio-border))' }}>
      <div style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: `hsl(var(${c}))`, display: 'flex', alignItems: 'center', gap: 5 }}>
        {up ? '▲' : '▼'} {up ? 'Top gainer' : 'Top loser'} · {tf}
      </div>
      <div style={{ ...Display, fontSize: 27, lineHeight: 1, color: `hsl(var(${c}))`, marginTop: 7 }}>{fmtPct(move)}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginTop: 5, minWidth: 0 }}>
        <span style={{ ...Mono, fontSize: 12, color: 'hsl(var(--studio-accent-soft))' }}>{inst.etfs[0].ticker}</span>
        <span style={{ fontFamily: 'var(--studio-font-sans)', fontSize: 10, color: 'hsl(var(--studio-text-subtle))', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{inst.name}</span>
      </div>
      <div style={{ marginTop: 6 }}><HeroCandles ticker={inst.etfs[0].ticker} range={tf} w={150} h={34} line /></div>
    </button>
  )
}

function LeverageDeskBody() {
  const { openTicker } = useStockDetail()
  const { instruments, sectors, count, issuers, loading } = useLeveragedEtfs()
  const [tf, setTf] = useState('1D')
  const [sector, setSector] = useState('all')
  const [sort, setSort] = useState('gain')   // 'gain' = biggest first (desc) | 'loss' = biggest drop first (asc)

  // Live moves for EVERY ETF, not just each instrument's primary (historical
  // mode → change1d/1w/1m/6m/1y). One batched hook call, refreshed each minute.
  // Siblings are fetched so a primary with no quote can borrow one (see below)
  // — ~116 tickers vs ~93, which is the same two /api/prices batches.
  const etfTickers = useMemo(
    () => instruments.flatMap((i) => i.etfs.map((e) => e.ticker)).filter(Boolean),
    [instruments],
  )
  const { prices, loading: pricesLoading } = useLivePrices(etfTickers, 60_000, 'historical')

  const field = CHANGE_FIELD[tf]

  // Filter by real Atlas sector → attach the selected-timeframe move → sort
  // desc (nulls last). While the initial price fill is in flight, keep catalog
  // order — ranking half-filled moves reshuffles the list on every landing
  // batch. One rank at settle; revisits seed from the price cache instantly.
  const rows = useMemo(() => {
    // A row with no quote on its primary sorts to the very BOTTOM (nulls last),
    // so one missing fund quote could bury a name that was up 10% that day. All
    // 2× funds on the same underlying track each other within a few tenths, so
    // borrowing a sibling's move keeps the row ranked honestly instead of
    // dropping it off the list. The row still DISPLAYS the primary.
    const moveFor = (inst) => {
      for (const e of inst.etfs) {
        const v = (prices[e.ticker] || {})[field]
        if (v != null) return v
      }
      return undefined
    }
    const list = instruments
      .filter((i) => sector === 'all' || i.sector === sector)
      .map((i) => ({ inst: i, move: moveFor(i) }))
    if (!pricesLoading) list.sort((a, b) => {
      if (a.move == null && b.move == null) return a.inst.underlying.localeCompare(b.inst.underlying)
      if (a.move == null) return 1
      if (b.move == null) return -1
      return sort === 'loss' ? a.move - b.move : b.move - a.move
    })
    return list
  }, [instruments, prices, field, sector, sort, pricesLoading])

  // Basket pill — average live 1D across instruments that have a quote.
  // '—' until the fill settles (a partial average ticks distractingly).
  const basket = useMemo(() => {
    if (pricesLoading) return null
    const vals = instruments.map((i) => prices[i.etfs[0]?.ticker]?.change1d).filter((v) => v != null)
    if (!vals.length) return null
    return vals.reduce((a, b) => a + b, 0) / vals.length
  }, [instruments, prices, pricesLoading])
  const basketUp = basket != null && basket >= 0

  // Leaders & Laggards = the highest + lowest move, independent of the chosen
  // sort direction. Held until the fill settles — a mid-fill band flips
  // tickers on every batch and remounts its HeroCandles chart fetches.
  let lead = null, lag = null
  if (!pricesLoading) {
    for (const r of rows) {
      if (r.move == null) continue
      if (!lead || r.move > lead.move) lead = r
      if (!lag || r.move < lag.move) lag = r
    }
    if (lead && lead === lag) lag = null
  }

  return (
    <TickerCtx.Provider value={openTicker}>
      <div style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column',
        background: 'radial-gradient(140% 75% at 50% -10%, hsl(var(--studio-background)) 0%, hsl(var(--studio-overlay)) 100%)' }}>
        {/* header — the DeskSwitcher is the eyebrow (UNIVERSE / LEVERAGE / GLOBAL) */}
        <div style={{ flexShrink: 0, padding: '20px 20px 0' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ minWidth: 0 }}><DeskSwitcher /></div>
            <SearchPill />
          </div>
          <div style={{ ...Display, fontSize: 33, color: 'hsl(var(--studio-text))', lineHeight: 1.04, marginTop: 6 }}>The Leverage Desk</div>
          {/* leaders & laggards — top gainer + top loser for the selected window */}
          {(lead || lag) && (
            <div style={{ display: 'flex', marginTop: 16, background: 'hsl(var(--studio-surface))', border: '1px solid hsl(var(--studio-border))', borderRadius: 'var(--studio-radius-lg)', overflow: 'hidden' }}>
              {lead && <BandCell row={lead} up tf={tf} onOpen={openTicker} />}
              {lag && <BandCell row={lag} up={false} tf={tf} onOpen={openTicker} />}
            </div>
          )}
          <div style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.1em', color: 'hsl(var(--studio-text-subtle))', marginTop: 12 }}>
            {count} ETFS · {issuers} ISSUERS · ATLAS 2× BASKET <span style={{ color: `hsl(var(${basketUp ? '--studio-success' : '--studio-danger'}))` }}>{basket == null ? '—' : fmtPct(basket)}</span>
          </div>
        </div>

        {/* column header — same GRID as the rows. Stock header is the sector
            filter dropdown; the move header is the timeframe dropdown + a tap
            ↑/↓ to flip the sort direction. Excel-style, no extra rows. */}
        <div style={{ ...GRID, padding: '18px 20px 7px' }}>
          <span />
          <span style={{ minWidth: 0 }}>
            <Dropdown value={sector} onChange={setSector} align="left" size={8.5} fallbackLabel="Stock"
              options={[{ value: 'all', label: 'All sectors' }, ...sectors.map((s) => ({ value: s.id, label: s.label }))]} />
          </span>
          <span style={HDR}>2× ETF</span>
          <span style={HDR}>Trend</span>
          <span style={{ justifySelf: 'end', display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            <Dropdown value={tf} onChange={setTf} align="right" size={8.5} options={TFS.map((k) => ({ value: k, label: k }))} />
            <button onClick={() => setSort((s) => (s === 'gain' ? 'loss' : 'gain'))} aria-label="Toggle sort direction"
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'hsl(var(--studio-accent))', fontSize: 12, lineHeight: 1 }}>
              {sort === 'loss' ? '↑' : '↓'}
            </button>
          </span>
        </div>
        <div className="atlas-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
          {loading ? (
            <div style={{ ...Mono, fontSize: 11, letterSpacing: '0.08em', color: 'hsl(var(--studio-text-subtle))', padding: '40px 20px', textAlign: 'center' }}>LOADING DESK…</div>
          ) : rows.length === 0 ? (
            <div style={{ ...Mono, fontSize: 11, letterSpacing: '0.08em', color: 'hsl(var(--studio-text-subtle))', padding: '40px 20px', textAlign: 'center' }}>NO INSTRUMENTS</div>
          ) : (
            rows.map((r, i) => (
              <DeskRow key={r.inst.underlying} rank={i + 1} inst={r.inst} move={r.move} tf={tf} onOpen={openTicker} />
            ))
          )}
          <div style={{ height: 14 }} />
        </div>

        <div style={{ flexShrink: 0, borderTop: '1px solid hsl(var(--studio-border) / 0.5)', background: 'hsl(var(--studio-background) / 0.72)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}>
          <TickerStrip />
        </div>
        <BottomNav />
      </div>
    </TickerCtx.Provider>
  )
}

export function LeverageDesk() {
  return (
    <div className="v3-root" style={{ height: 'var(--v3-vh, 100dvh)', display: 'flex', flexDirection: 'column' }}>
      <StockDetailProvider>
        <LeverageDeskBody />
      </StockDetailProvider>
    </div>
  )
}
